/** Matches backend NotificationCtaAction enum. */
export const NotificationCtaAction = {
  Homepage: 1,
  HolidayLanding: 2,
  HolidayListing: 3,
  HolidayDetails: 4,
  ActivityLanding: 5,
  ActivityListing: 6,
  ActivityDetails: 7,
  Cart: 13,
  CountryCityPage: 14,
  Deals: 15,
  SeasonalDeals: 16,
  CouponListing: 17,
  CouponDetails: 18,
  HolidayBooking: 19,
  ActivityBooking: 20,
};

const LISTING_QUERY_KEYS = [
  "CountryId",
  "countryId",
  "CountryName",
  "countryName",
  "CityId",
  "cityId",
  "CityName",
  "cityName",
  "StateId",
  "stateId",
  "StateName",
  "stateName",
  "RegionId",
  "regionId",
  "RegionName",
  "regionName",
  "PackageTypeId",
  "packageTypeId",
  "ActivityTypeId",
  "activityTypeId",
  "SubTypeId",
  "subTypeId",
  "ActivityId",
  "activityId",
  "Keywords",
  "keywords",
  "Month",
  "month",
  "couponCode",
  "CouponCode",
  "TempBookingId",
  "tempBookingId",
  "CartId",
  "cartId",
  "BookingId",
  "bookingId",
];

export function parseNotificationCtaPayload(payload) {
  if (!payload) return {};

  if (typeof payload === "object" && !Array.isArray(payload)) {
    return payload;
  }

  if (typeof payload === "string") {
    const trimmed = payload.trim();
    if (!trimmed) return {};

    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed;
      }
    } catch {
      return { slug: trimmed };
    }
  }

  return {};
}

function pickSlug(data = {}, extraKeys = []) {
  const keys = [
    "slug",
    "Slug",
    "packageSlug",
    "PackageSlug",
    "holidaySlug",
    "HolidaySlug",
    "activitySlug",
    "ActivitySlug",
    "dealSlug",
    "DealSlug",
    "couponSlug",
    "CouponSlug",
    "placeSlug",
    "PlaceSlug",
    ...extraKeys,
  ];

  for (const key of keys) {
    const value = data[key];
    if (value !== undefined && value !== null && String(value).trim()) {
      return String(value).trim();
    }
  }

  return "";
}

function appendListingQuery(basePath, data = {}) {
  const params = new URLSearchParams();
  const usedKeys = new Set();

  LISTING_QUERY_KEYS.forEach((key) => {
    const value = data[key];
    if (value === undefined || value === null || value === "") return;

    const canonicalKey = key.charAt(0).toUpperCase() === key.charAt(0) ? key : key;
    const paramKey =
      key === "countryId"
        ? "CountryId"
        : key === "cityId"
          ? "CityId"
          : key === "stateId"
            ? "StateId"
            : key === "regionId"
              ? "RegionId"
              : key === "packageTypeId"
                ? "PackageTypeId"
                : key === "activityTypeId"
                  ? "ActivityTypeId"
                  : key === "subTypeId"
                    ? "SubTypeId"
                    : key === "activityId"
                      ? "ActivityId"
                      : key === "keywords"
                        ? "Keywords"
                        : key === "month"
                          ? "Month"
                          : key === "couponCode"
                            ? "couponCode"
                            : key === "tempBookingId"
                              ? "TempBookingId"
                              : key === "cartId"
                                ? "CartId"
                                : key === "bookingId"
                                  ? "BookingId"
                                  : key;

    if (usedKeys.has(paramKey)) return;
    usedKeys.add(paramKey);
    params.set(paramKey, String(value));
  });

  Object.entries(data).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    if (
      [
        "slug",
        "Slug",
        "packageSlug",
        "PackageSlug",
        "dealSlug",
        "DealSlug",
        "couponSlug",
        "CouponSlug",
        "ctaAction",
        "CtaAction",
      ].includes(key)
    ) {
      return;
    }
    if (LISTING_QUERY_KEYS.includes(key)) return;

    if (!usedKeys.has(key)) {
      usedKeys.add(key);
      params.set(key, String(value));
    }
  });

  const query = params.toString();
  return query ? `${basePath}?${query}` : basePath;
}

/**
 * Build an app path (without /in prefix) from notification CTA fields.
 * Returns empty string when navigation is not possible.
 */
export function buildNotificationHref(ctaAction, ctaPayload) {
  const action = Number(ctaAction);
  if (!Number.isFinite(action) || action <= 0) return "";

  const data = parseNotificationCtaPayload(ctaPayload);

  switch (action) {
    case NotificationCtaAction.Homepage:
      return "/";

    case NotificationCtaAction.HolidayLanding:
      return "/holiday";

    case NotificationCtaAction.HolidayListing:
      return appendListingQuery("/holiday/list", data);

    case NotificationCtaAction.HolidayDetails: {
      const slug = pickSlug(data);
      return slug ? `/holiday/${encodeURIComponent(slug)}` : "/holiday";
    }

    case NotificationCtaAction.ActivityLanding:
      return "/activity";

    case NotificationCtaAction.ActivityListing:
      return appendListingQuery("/activity/list", data);

    case NotificationCtaAction.ActivityDetails: {
      const slug = pickSlug(data);
      return slug ? `/activity/${encodeURIComponent(slug)}` : "/activity/list";
    }

    case NotificationCtaAction.Cart:
      return "/cart";

    case NotificationCtaAction.CountryCityPage: {
      const slug = pickSlug(data);
      return slug ? `/place/${encodeURIComponent(slug)}` : "";
    }

    case NotificationCtaAction.Deals:
      return "/deals";

    case NotificationCtaAction.SeasonalDeals: {
      const slug = pickSlug(data);
      return slug ? `/deals/${encodeURIComponent(slug)}` : "/deals";
    }

    case NotificationCtaAction.CouponListing:
      return "/coupons";

    case NotificationCtaAction.CouponDetails: {
      const slug = pickSlug(data);
      return slug ? `/coupons/${encodeURIComponent(slug)}` : "/coupons";
    }

    case NotificationCtaAction.HolidayBooking:
      return appendListingQuery("/holiday/package-booking", data);

    case NotificationCtaAction.ActivityBooking:
      return appendListingQuery("/activity/package-booking", data);

    default:
      return "";
  }
}

export function canNavigateNotification(item) {
  return Boolean(buildNotificationHref(item?.ctaAction, item?.ctaPayload));
}
